/**
 * Share Image Service
 *
 * Generates shareable images for game results and handles sharing functionality.
 * Uses Canvas API for image generation and Capacitor Share/Filesystem for sharing.
 */

import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { debugLog, debugError } from '../utils/debugUtils';
import logoImg from '../assets/images/logo.png';

class ShareImageService {
    constructor() {
        this.logoLoaded = false;
        this.logoImage = null;
    }

    /**
     * Preload the logo image
     * @returns {Promise<HTMLImageElement>}
     */
    async loadLogo() {
        if (this.logoLoaded && this.logoImage) {
            return this.logoImage;
        }

        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                this.logoImage = img;
                this.logoLoaded = true;
                resolve(img);
            };
            img.onerror = (err) => {
                debugError('[ShareImageService] Failed to load logo:', err);
                reject(err);
            };
            img.src = logoImg;
        });
    }

    /**
     * Wrap text to fit within a given width
     * @param {CanvasRenderingContext2D} ctx
     * @param {string} text
     * @param {number} x
     * @param {number} y
     * @param {number} maxWidth
     * @param {number} lineHeight
     */
    wrapText(ctx, text, x, y, maxWidth, lineHeight) {
        const words = text.split(' ');
        let line = '';
        let currentY = y;

        for (let i = 0; i < words.length; i++) {
            const testLine = line + words[i] + ' ';
            const metrics = ctx.measureText(testLine);
            if (metrics.width > maxWidth && i > 0) {
                ctx.fillText(line.trim(), x, currentY);
                line = words[i] + ' ';
                currentY += lineHeight;
            } else {
                line = testLine;
            }
        }
        ctx.fillText(line.trim(), x, currentY);
    }

    /**
     * Draw an avatar (emoji or placeholder) on canvas
     * @param {CanvasRenderingContext2D} ctx
     * @param {string} avatar - Emoji or image URL
     * @param {number} x - Center X position
     * @param {number} y - Center Y position
     * @param {number} size - Avatar size
     */
    drawAvatar(ctx, avatar, x, y, size) {
        // Check if avatar is an emoji (not a URL)
        const isEmoji = typeof avatar === 'string' && !avatar.startsWith('http') && !avatar.startsWith('/');

        // Draw circle background
        ctx.save();
        ctx.beginPath();
        ctx.arc(x, y, size / 2, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.fill();
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.closePath();

        if (isEmoji && avatar) {
            // Draw emoji centered
            ctx.font = `${size * 0.6}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = '#ffffff';
            ctx.fillText(avatar, x, y);
        }
        ctx.restore();
    }

    /**
     * Create a shareable image for the game winner
     * @param {Object} params
     * @param {number} params.winnerTeamId - ID of the winning team (1 or 2)
     * @param {Array} params.players - Array of 4 players
     * @param {number} params.gamePoints - Points needed to win (2 or 4)
     * @param {Function} params.t - Translation function
     * @returns {Promise<string>} - Base64 data URL of the image
     */
    async createWinnerShareImage({ winnerTeamId, players, gamePoints, t }) {
        return new Promise(async (resolve, reject) => {
            try {
                // Load logo first
                await this.loadLogo();

                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Instagram Story resolution (9:16)
                const width = 1080;
                const height = 1920;

                canvas.width = width;
                canvas.height = height;

                // Background gradient - dark theme with gold accents
                const gradient = ctx.createLinearGradient(0, 0, 0, height);
                gradient.addColorStop(0, '#1a1a2e');   // Dark blue
                gradient.addColorStop(0.4, '#16213e'); // Navy
                gradient.addColorStop(0.7, '#0f3460'); // Deep blue
                gradient.addColorStop(1, '#1a1a2e');   // Dark blue
                ctx.fillStyle = gradient;
                ctx.fillRect(0, 0, width, height);

                // Add some decorative elements - Spanish card suit symbols
                ctx.globalAlpha = 0.08;
                ctx.textAlign = 'center';

                // Oros (gold coins) - draw circles
                ctx.fillStyle = '#FFD700';
                ctx.beginPath();
                ctx.arc(150, 280, 80, 0, Math.PI * 2);
                ctx.fill();

                // Copas (cups) - use trophy emoji
                ctx.font = '180px Arial';
                ctx.fillStyle = '#FFD700';
                ctx.fillText('🏆', 930, 520);

                // Espadas (swords) - use crossed swords emoji
                ctx.fillText('⚔️', 200, 1420);

                // Bastos (clubs/batons) - draw a simple baton shape
                ctx.fillStyle = '#8B4513';
                ctx.save();
                ctx.translate(880, 1520);
                ctx.rotate(-Math.PI / 6);
                ctx.fillRect(-20, -100, 40, 200);
                ctx.beginPath();
                ctx.arc(0, -100, 30, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();

                ctx.globalAlpha = 1;

                // Draw logo at the top
                if (this.logoImage) {
                    const maxLogoWidth = 500;
                    const scale = Math.min(1, maxLogoWidth / this.logoImage.width);
                    const logoW = Math.round(this.logoImage.width * scale);
                    const logoH = Math.round(this.logoImage.height * scale);
                    ctx.drawImage(this.logoImage, (width - logoW) / 2, 100, logoW, logoH);
                }

                // Victory title
                ctx.shadowColor = 'rgba(255, 215, 0, 0.5)';
                ctx.shadowBlur = 30;
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 0;

                ctx.font = 'bold 90px Arial, sans-serif';
                ctx.fillStyle = '#FFD700';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                const victoryText = t ? t('game.winner_title') : '¡Victoria!';
                ctx.fillText(victoryText, width / 2, 550);

                // Reset shadow
                ctx.shadowBlur = 0;

                // Winning team text
                ctx.font = '50px Arial, sans-serif';
                ctx.fillStyle = '#ffffff';
                const teamText = t ? t('game.winner_team', { teamId: winnerTeamId }) : `Pareja ${winnerTeamId} gana la partida`;
                ctx.fillText(teamText, width / 2, 670);

                // Get winning players
                const winningPlayers = winnerTeamId === 1
                    ? [players[0], players[2]]
                    : [players[1], players[3]];

                // Draw winning players
                const avatarSize = 180;
                const playerY = 900;
                const spacing = 300;

                winningPlayers.forEach((player, index) => {
                    const playerX = width / 2 + (index === 0 ? -spacing / 2 : spacing / 2);

                    // Draw avatar
                    this.drawAvatar(ctx, player?.avatar, playerX, playerY, avatarSize);

                    // Draw player name
                    ctx.font = 'bold 40px Arial, sans-serif';
                    ctx.fillStyle = '#ffffff';
                    ctx.textAlign = 'center';
                    const playerName = player?.name || (t ? t('setup.player_placeholder_1') : 'Jugador');
                    ctx.fillText(playerName, playerX, playerY + avatarSize / 2 + 30);
                });

                // Draw final score
                const loserTeamId = winnerTeamId === 1 ? 2 : 1;
                const winnerScore = gamePoints;
                const loserScore = 0; // When someone wins, the loser hasn't reached the points

                ctx.font = 'bold 120px Arial, sans-serif';
                ctx.fillStyle = '#FFD700';
                ctx.shadowColor = 'rgba(255, 215, 0, 0.3)';
                ctx.shadowBlur = 20;
                ctx.fillText(`${winnerScore} - ${loserScore}`, width / 2, 1150);
                ctx.shadowBlur = 0;

                // Score label
                ctx.font = '40px Arial, sans-serif';
                ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
                const scoreLabel = t ? t('share.final_score') : 'Resultado final';
                ctx.fillText(scoreLabel, width / 2, 1300);

                // Share message
                ctx.font = '44px Arial, sans-serif';
                ctx.fillStyle = '#ffffff';
                const shareMsg = t ? t('share.winner_message') : '¡Hemos ganado la partida de Ronda y Báciga!';
                this.wrapText(ctx, shareMsg, width / 2, 1450, 900, 60);

                // Footer with app promotion
                ctx.font = 'italic 36px Arial, sans-serif';
                ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
                const footerText = t ? t('share.footer') : 'Descarga Ronda y Báciga y juega con tus amigos';
                ctx.fillText(footerText, width / 2, 1750);

                resolve(canvas.toDataURL('image/png'));
            } catch (error) {
                debugError('[ShareImageService] Error creating share image:', error);
                reject(error);
            }
        });
    }

    /**
     * Convert base64 data URL to Blob
     * @param {string} dataUrl
     * @returns {Blob}
     */
    dataUrlToBlob(dataUrl) {
        const arr = dataUrl.split(',');
        const mime = arr[0].match(/:(.*?);/)[1];
        const bstr = atob(arr[1]);
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new Blob([u8arr], { type: mime });
    }

    /**
     * Share the winner image
     * @param {Object} params - Same params as createWinnerShareImage
     * @returns {Promise<boolean>} - True if shared successfully
     */
    async shareWinnerImage(params) {
        try {
            debugLog('[ShareImageService] Creating share image...');
            const dataUrl = await this.createWinnerShareImage(params);

            if (Capacitor.isNativePlatform()) {
                // Native platform - use Capacitor Filesystem + Share
                return await this.shareNative(dataUrl, params.t);
            } else {
                // Web platform - use Web Share API
                return await this.shareWeb(dataUrl, params.t);
            }
        } catch (error) {
            debugError('[ShareImageService] Error sharing image:', error);
            // Fallback: download the image
            await this.downloadImage(dataUrl);
            return false;
        }
    }

    /**
     * Share on native platforms using Capacitor
     * @param {string} dataUrl
     * @param {Function} t - Translation function
     */
    async shareNative(dataUrl, t) {
        try {
            // Save image to filesystem
            const fileName = `ronda_baciga_winner_${Date.now()}.png`;
            const base64Data = dataUrl.split(',')[1];

            const result = await Filesystem.writeFile({
                path: fileName,
                data: base64Data,
                directory: Directory.Cache
            });

            debugLog('[ShareImageService] File saved:', result.uri);

            // Share the file
            const shareTitle = t ? t('share.title') : '¡Victoria en Ronda y Báciga!';
            const shareText = t ? t('share.text') : 'Mira quién ha ganado la partida';

            await Share.share({
                title: shareTitle,
                text: shareText,
                url: result.uri,
                dialogTitle: shareTitle
            });

            debugLog('[ShareImageService] Share dialog opened');
            return true;
        } catch (error) {
            debugError('[ShareImageService] Native share error:', error);
            throw error;
        }
    }

    /**
     * Share on web using Web Share API
     * @param {string} dataUrl
     * @param {Function} t - Translation function
     */
    async shareWeb(dataUrl, t) {
        try {
            const shareTitle = t ? t('share.title') : '¡Victoria en Ronda y Báciga!';
            const shareText = t ? t('share.text') : 'Mira quién ha ganado la partida';

            // Check if Web Share API with files is supported
            if (navigator.canShare && navigator.share) {
                const blob = this.dataUrlToBlob(dataUrl);
                const file = new File([blob], 'ronda_baciga_winner.png', { type: 'image/png' });

                const shareData = {
                    title: shareTitle,
                    text: shareText,
                    files: [file]
                };

                if (navigator.canShare(shareData)) {
                    await navigator.share(shareData);
                    debugLog('[ShareImageService] Web share completed');
                    return true;
                }
            }

            // Fallback: try sharing without file
            if (navigator.share) {
                await navigator.share({
                    title: shareTitle,
                    text: shareText
                });
                // Also download the image
                this.downloadImage(dataUrl);
                return true;
            }

            // No share API available - download
            this.downloadImage(dataUrl);
            return false;
        } catch (error) {
            if (error.name === 'AbortError') {
                // User cancelled sharing
                debugLog('[ShareImageService] User cancelled sharing');
                return false;
            }
            debugError('[ShareImageService] Web share error:', error);
            throw error;
        }
    }

    /**
     * Download the image as a fallback
     * @param {string} dataUrl
     */
    downloadImage(dataUrl) {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `ronda_baciga_winner_${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        debugLog('[ShareImageService] Image downloaded');
    }
}

// Export singleton instance
export const shareImageService = new ShareImageService();
export default shareImageService;
